<template>
  <section class="advertising-row" :class="loading ? 'advertising-row--loading' : ''">
    <div class="advertising-row__header">
      <div>
        <div class="advertising-row__type">{{ rowData.type === 'carousel' ? '轮播广告' : '均匀分布广告' }}</div>
        <div class="advertising-row__desc">
          {{ rowData.type === 'carousel' ? '多条广告按顺序轮播展示' : '多条广告在同一行均匀展示' }}
        </div>
      </div>
      <div class="advertising-row__actions">
        <HpsfirButton v-if="!isFirst" variant="outline" size="sm" @click="() => moveRow('up')">
          <el-icon><ArrowUpBold /></el-icon> 上移
        </HpsfirButton>
        <HpsfirButton v-if="!isLast" variant="outline" size="sm" @click="() => moveRow('down')">
          <el-icon><ArrowDownBold /></el-icon> 下移
        </HpsfirButton>
        <button class="advertising-row__action-btn advertising-row__action-btn--danger" type="button" @click.stop="openDeleteDialog">
          <el-icon><Delete /></el-icon> 删除
        </button>
      </div>
    </div>

    <div
      class="advertising-row__content"
      :class="rowData.type === 'distribute' ? 'advertising-row__content--grid' : 'advertising-row__content--list'"
    >
      <AdvertisingItem
        v-for="(item, inx) in datas"
        :key="item.id"
        :data="item"
        :is-first="inx === 0"
        :is-last="inx === datas.length - 1"
        :is-h="rowData.type === 'distribute'"
        @change="() => init()"
      />
      <button class="advertising-row__add-item" type="button" @click="() => addDialogRef?.showDialog()">
        <el-icon><Plus /></el-icon>
        添加一条广告
      </button>
    </div>

    <div v-if="loading" class="advertising-row__loading-tip">处理中...</div>

    <HpsfirProForm :is-dialog="true" ref="addDialogRef" title="添加一条" :items="items" @ok="handleAdd"/>

    <HpsfirProDrawer
      v-model:open="deleteDialogVisible"
      title="删除广告行"
      width="420px"
      max-width="calc(100vw - 32px)"
    >
      <div class="advertising-row__delete-confirm">
        <div class="advertising-row__delete-title">确定要删除这一行广告吗？</div>
        <div class="advertising-row__delete-desc">该行下的广告条目也会被删除，删除后无法恢复，请谨慎操作。</div>
      </div>
      <template #footer>
        <HpsfirButton variant="outline" @click="deleteDialogVisible = false">取消</HpsfirButton>
        <HpsfirButton variant="destructive" :loading="loading" @click="confirmDeleteRow">确定删除</HpsfirButton>
      </template>
    </HpsfirProDrawer>
  </section>
</template>

<script lang="ts" setup>
import {
  useBaseAsync,
  type HpsfirProFormItem,
} from 'hpsfir-managebase-ui';

import AdvertisingItem from './AdvertisingItem.vue';
import { useApi } from '@/hooks/useApi';
import { ref } from 'vue';
import { ElMessage } from 'element-plus';

const loading = ref(false);
const deleteDialogVisible = ref(false);

const props = defineProps<{
  rowData: any;
  isFirst?: boolean;
  isLast?: boolean;
}>();

const addDialogRef = ref<{ reset?(): void; setOneFormState?(name: string, value: unknown): void }>();

const typeOp: {
    value: any;
    label: string;
}[] = [
  {
    label: '小程序',
    value: 'weapp',
  },
  {
    label: '公众号文章',
    value: 'wx',
  },
];

const items: HpsfirProFormItem[] = [
  {
    name: 'image',
    label: '图片',
    rule: [{ required: true }],
    type: 'image',
    action: useApi().uploadImageUrl,
    multiple: false,
    onSuccess(data) {
      console.log(data.data[0]);
      addDialogRef.value?.setOneFormState('image', data.data[0]);
    },
  },
  {
    type: 'select',
    label: '类型',
    name: 'type',
    options: typeOp,
    rule: [{ required: true }],
  },
  {
    type: 'string',
    label: 'URL/Path',
    name: 'url',
    rule: [{ required: true }],
  },
  {
    type: 'string',
    label: 'appid',
    name: 'appid',
  },
];

const datas = ref<any[]>([]);

const emits = defineEmits(['change']);

function withTimeout<T>(promise: Promise<T>, message = '广告数据加载超时') {
  return Promise.race([
    promise,
    new Promise<T>((resolve, reject) => {
      void resolve;
      window.setTimeout(() => reject(new Error(message)), 15000);
    }),
  ]);
}

const init = useBaseAsync(async() => {
  try {
    loading.value = true;
    const res = await withTimeout(useApi().advertisingApi.listAdvertisingadvertising({
      conditions: {
        rowId: props.rowData.id,
      },
    }));
    datas.value = res?.data?.data || [];
  } catch (error) {
    console.log(error);
    ElMessage.error(error instanceof Error ? error.message : '广告数据加载失败');
  } finally {
    loading.value = false;
  }
});

const moveRow = useBaseAsync(async(type: 'up' | 'down') => {
  try {
    loading.value = true;
    await withTimeout(useApi().advertisingApi.moveAdvertisingRowadvertising({
      advertisingRowId: props.rowData.id,
      up: type === 'up',
    }), '移动广告行超时');
    emits('change');
  } catch (error) {
    console.log(error);
    ElMessage.error(error instanceof Error ? error.message : '移动广告行失败');
  } finally {
    loading.value = false;
  }
});

const openDeleteDialog = () => {
  deleteDialogVisible.value = true;
};

const confirmDeleteRow = async() => {
  try {
    loading.value = true;
    await withTimeout(useApi().advertisingApi.delAdvertisingRowadvertising(props.rowData.id), '删除广告行超时');
    deleteDialogVisible.value = false;
    ElMessage.success('删除成功');
    emits('change');
  } catch (error) {
    console.log(error);
    ElMessage.error(error instanceof Error ? error.message : '删除广告行失败');
  } finally {
    loading.value = false;
  }
};

const handleAdd = useBaseAsync(async(data: any) => {
  try {
    await withTimeout(useApi().advertisingApi.addAdvertisingadvertising({
      advertisingAdminVo: {
        rowId: props.rowData.id,
        sequence: datas.value.length + 1,
        ...data,
      },
    }), '添加广告超时');
    ElMessage.success('添加成功');
    addDialogRef.value?.reset();
    init();
  } catch (error) {
    console.log(error);
    ElMessage.error(error instanceof Error ? error.message : '添加广告失败');
  }
});

init();
</script>

<style lang="scss">
.advertising-row {
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--border, #e5e7eb) 76%, transparent);
  border-radius: 20px;
  background: var(--card, #fff);
  box-shadow: 0 16px 36px -28px rgba(15, 23, 42, 0.45);

  &--loading {
    opacity: 0.72;
  }

  &__loading-tip {
    padding: 0 16px 14px;
    color: var(--muted-foreground, #6b7280);
    font-size: 12px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 18px;
    border-bottom: 1px solid color-mix(in srgb, var(--border, #e5e7eb) 68%, transparent);
    background: color-mix(in srgb, var(--muted, #f3f4f6) 36%, transparent);
  }

  &__type {
    font-size: 16px;
    font-weight: 800;
    color: var(--foreground, #111827);
  }

  &__desc {
    margin-top: 4px;
    font-size: 13px;
    color: var(--muted-foreground, #6b7280);
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
  }

  &__action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-height: 32px;
    padding: 0 12px;
    border: 1px solid color-mix(in srgb, var(--border, #d1d5db) 76%, transparent);
    border-radius: 8px;
    background: var(--card, #fff);
    color: var(--foreground, #111827);
    cursor: pointer;
    font-size: 13px;
    transition: all 0.16s ease;

    &:hover {
      background: color-mix(in srgb, var(--muted, #f3f4f6) 60%, transparent);
    }
  }

  &__action-btn--danger {
    border-color: color-mix(in srgb, #ef4444 36%, var(--border, #d1d5db));
    color: #dc2626;

    &:hover {
      background: color-mix(in srgb, #ef4444 10%, transparent);
      color: #b91c1c;
    }
  }

  &__content {
    display: grid;
    gap: 12px;
    padding: 16px;
  }

  &__content--list {
    grid-template-columns: 1fr;
  }

  &__content--grid {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }

  &__add-item {
    display: flex;
    min-height: 96px;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 1px dashed color-mix(in srgb, var(--border, #d1d5db) 85%, transparent);
    border-radius: 16px;
    background: color-mix(in srgb, var(--muted, #f3f4f6) 30%, transparent);
    color: var(--muted-foreground, #6b7280);
    cursor: pointer;
    font-weight: 600;
    transition: all 0.18s ease;

    &:hover {
      border-color: color-mix(in srgb, var(--primary, #111827) 45%, var(--border, #d1d5db));
      color: var(--foreground, #111827);
      background: color-mix(in srgb, var(--primary, #111827) 6%, var(--card, #fff));
    }
  }
}

@media (max-width: 767px) {
  .advertising-row {
    border-radius: 16px;

    &__header {
      align-items: stretch;
      flex-direction: column;
      padding: 14px;
    }

    &__actions {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      width: 100%;
    }

    &__actions > * {
      justify-content: center;
      min-width: 0;
    }

    &__content {
      padding: 12px;
    }

    &__content--grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
